import { ConvexError } from 'convex/values';
import {
  getGeneralAgentUploadMimeType,
  getUploadMediaType,
} from './composer-upload-accept';
import type { createComposerUploadLifecycle } from './composer-upload-lifecycle';
import {
  BrowserUploadError,
  uploadBlobToBrowserSession,
} from '@/lib/uploads/browser-multipart-upload';
import { hashBrowserUploadBody } from '@/lib/uploads/browser-upload-hash';
import {
  COMPOSER_UPLOAD_MAX_BYTES,
  ComposerUploadTooLargeError,
  composerUploadRoute,
} from '@/lib/uploads/composer-upload-route';
import {
  uploadComposerPut,
  type ComposerPutSession,
} from '@/lib/uploads/composer-put-upload';
import { uploadViaProxy } from '@/lib/uploads/upload-via-proxy';
import type {
  UploadSessionJournal,
  UploadSessionJournalEntry,
} from '@/lib/uploads/upload-manager/upload-session-journal';
import type { useComposerHandleGeneralUploadDependencies } from './composer-input/use-composer-handle-general-upload-types';

type Services = Parameters<typeof createComposerUploadLifecycle>[0];
type UploadDependencies = useComposerHandleGeneralUploadDependencies;
type Deps = Pick<
  UploadDependencies,
  'recordComposerUploads' | 'orgSlug' | 'composerPutUploadCalls'
> & {
  /** Known in the composer; uploads resumed at app level go without it. */
  organizationId?: UploadDependencies['organizationId'];
  /** A plain call: the composer's React mutation and an app-level client both fit. */
  generatePrivateUploadUrlForMedia: (
    args: Parameters<UploadDependencies['generatePrivateUploadUrlForMedia']>[0]
  ) => ReturnType<UploadDependencies['generatePrivateUploadUrlForMedia']>;
  activeOrgThreadId?: UploadDependencies['activeOrgThreadId'];
  journal: UploadSessionJournal;
  /**
   * A fresh capability for a session resumed after a reload, which browser
   * storage keeps by id only (#4844). Without it such a session is not
   * resumed and the upload starts over in a fresh one.
   */
  reissueBrowserUploadSessionCapability?: (args: {
    sessionId: string;
  }) => Promise<{ uploadUrl: string }>;
};

/**
 * The control URL of a journal session, asking the server for a fresh one
 * when only its id is known. Null when the server refuses it (gone, settled,
 * not this member's): the upload goes on in a fresh session. Transport
 * failures throw, keeping the session for a retry.
 */
async function sessionControlUrl(
  session: UploadSessionJournalEntry,
  reissue: Deps['reissueBrowserUploadSessionCapability']
): Promise<string | null> {
  if (session.uploadUrl) return session.uploadUrl;
  if (!session.sessionId || !reissue) return null;
  try {
    return (await reissue({ sessionId: session.sessionId })).uploadUrl;
  } catch (error) {
    if (error instanceof ConvexError) return null;
    throw error;
  }
}

/**
 * Upload and register one composer file. Plain functions, so the composer
 * and the app-level background upload manager (#4844) run the same path:
 * multipart over 64 MiB (resumable by part through the journal), a single
 * signed PUT, or the legacy proxy.
 */
export function createComposerUploadServices(
  deps: Deps
): Pick<Services, 'upload' | 'register'> {
  const {
    orgSlug,
    organizationId,
    generatePrivateUploadUrlForMedia,
    recordComposerUploads,
    composerPutUploadCalls,
    activeOrgThreadId,
    journal,
    reissueBrowserUploadSessionCapability: reissue,
  } = deps;
  // Sessions whose bytes are stored but whose `complete` response was lost:
  // a retry of the same upload completes them instead of uploading again.
  const storedSessions = new Map<string, ComposerPutSession>();
  return {
    upload: async ({ file, attachment, category }, signal, onProgress) => {
      signal.throwIfAborted();
      const contentType = getGeneralAgentUploadMimeType(file);
      const reserve = {
        orgSlug,
        ...(organizationId ? { organizationId } : {}),
        mediaType: getUploadMediaType(file),
        flow: 'org-chat-composer',
      };
      const completed = {
        uploadId: attachment.uploadId,
        filename: file.name,
        mimeType: contentType,
        sizeBytes: file.size,
        previewUrl: attachment.previewUrl,
      };
      if (file.size > COMPOSER_UPLOAD_MAX_BYTES)
        throw new ComposerUploadTooLargeError();
      if (composerUploadRoute(file.size) === 'multipart') {
        const uploadId = attachment.uploadId;
        // A session opened before a reload is resumed by part; if it has
        // expired meanwhile, the upload starts once more in a fresh one.
        for (let fresh = false; ; fresh = true) {
          let session = fresh ? undefined : journal.get(uploadId);
          let uploadUrl = session
            ? await sessionControlUrl(session, reissue)
            : null;
          signal.throwIfAborted();
          if (session && !uploadUrl) {
            // Refused: its parts are no use, so it starts over.
            journal.close(uploadId);
            session = undefined;
          } else if (session && uploadUrl !== session.uploadUrl) {
            // Reissued: the fresh capability stays in memory only.
            session = { ...session, uploadUrl: uploadUrl ?? undefined };
            journal.open(uploadId, session);
          }
          const resuming = Boolean(session);
          if (!session || !uploadUrl) {
            // The upload proxy refuses one body over 100 MB: send 64 MiB parts.
            const opened = await generatePrivateUploadUrlForMedia({
              ...reserve,
              filename: file.name,
              contentType,
            });
            uploadUrl = opened.uploadUrl;
            session = {
              uploadUrl,
              storageKey: opened.storageKey,
              completedParts: [],
            };
            journal.open(uploadId, session);
          }
          signal.throwIfAborted();
          try {
            await uploadBlobToBrowserSession(uploadUrl, file, {
              contentType,
              signal,
              identity: session.identity,
              completedParts: session.completedParts,
              onIdentity: (identity) => journal.identified(uploadId, identity),
              onPartDone: (partNumber) =>
                journal.partDone(uploadId, partNumber),
              onProgress: ({ loadedBytes, totalBytes }) =>
                onProgress(loadedBytes, totalBytes),
            });
          } catch (error) {
            const aborted =
              signal.aborted ||
              (error instanceof BrowserUploadError &&
                error.code === 'UPLOAD_ABORTED');
            if (aborted || !resuming || fresh) {
              if (!aborted) journal.close(uploadId);
              throw error;
            }
            journal.close(uploadId);
            continue;
          }
          journal.close(uploadId);
          return { ...completed, storageKey: session.storageKey };
        }
      }
      if (composerPutUploadCalls && orgSlug) {
        // Two calls: start (reserve + claim + presign; it waits for the
        // worker hash, which it pins) and complete (verify + register).
        const uploadId = attachment.uploadId;
        const stored = await uploadComposerPut({
          file,
          contentType,
          identity: () => hashBrowserUploadBody(file),
          resume: storedSessions.get(uploadId),
          onSession: (session) =>
            session
              ? storedSessions.set(uploadId, session)
              : storedSessions.delete(uploadId),
          reserve: { ...reserve, orgSlug },
          registration: {
            ...(activeOrgThreadId ? { orgThreadId: activeOrgThreadId } : {}),
            uploadId: attachment.uploadId,
            ...(category ? { referenceCategory: category } : {}),
          },
          calls: composerPutUploadCalls,
          signal,
          onProgress,
        });
        return {
          ...completed,
          storageKey: stored.storageKey,
          ...(stored.registered ? { registered: stored.registered } : {}),
        };
      }
      // Hash in a worker while the presign round trip runs.
      const identity = hashBrowserUploadBody(file);
      identity.catch(() => undefined);
      const { uploadUrl, storageKey } = await generatePrivateUploadUrlForMedia({
        ...reserve,
        filename: file.name,
        contentType,
      });
      signal.throwIfAborted();
      const result = await uploadViaProxy(uploadUrl, file, contentType, {
        signal,
        identity,
      });
      if (!result.ok) throw new Error('UPLOAD_FAILED');
      return { ...completed, storageKey };
    },
    register: async (completed, category) => {
      // Registered inside `complete` already; otherwise register here.
      if (completed.registered) return completed.registered;
      const records = await recordComposerUploads([completed], category);
      const registered = records.get(completed.storageKey);
      if (!registered) throw new Error('UPLOAD_REGISTRATION_INCOMPLETE');
      return registered;
    },
  };
}
